import React from 'react';
import { useStore } from 'effector-react';
import { randomId, toPercent } from 'pano-utils';
import { useImmer } from 'use-immer';
import { useMountedState } from 'react-use';
import {
  LibApiType,
  LibType,
  type LibUploadingMap,
  QiniuTokenType,
} from '@types';
import { queryClient, queryKeys } from '@queryClient';
import { $member } from '@stores/memberStores';
import uploadToQiniu from '@utils/uploadToQiniu';
import { sendError, sendWarning } from '@components/Noti';
import { $libAlbumId } from '../stores/libState';
import { baseSpaceCheck, remainSpaceCheck } from '../actions/spaceCheck';
import libSelectFiles from '../utils/libSelectFiles';
import UploadButton from '../components/UploadButton';
import addToLib from '../actions/addToLib';
import { LIB_SIZE_LIMIT } from '../constants';
import seqFrameCheck from './seqFrameCheck';
import refetchLibSpace from '../utils/refetchLibSpace';

function UploadButtonSeqFrame() {
  const albumId = useStore($libAlbumId);
  const [itemMap, setMap] = useImmer<LibUploadingMap>(new Map());
  const isMounted = useMountedState();
  const setItemMap: typeof setMap = (arg) => {
    if (isMounted()) {
      setMap(arg);
    }
  };

  const handleUplpad = async () => {
    await baseSpaceCheck();
    const { files, size } = await libSelectFiles({
      accept: ['image/png', 'image/jpeg'],
      sizeLimit: LIB_SIZE_LIMIT[LibType.sequenceFrame],
    });
    await remainSpaceCheck(size);
    const id = randomId('seq_');
    try {
      const { uid } = $member.getState().property!;
      const prefix = `/${uid}/${LibApiType.seqFrame}`;
      const { list, seqName, seqSuffix, totalSize } =
        await seqFrameCheck(files);
      setItemMap((draft) => {
        draft.set(id, { id, name: seqName, progress: '等待中' });
      });
      const seqUrl = `${prefix}/${id}`;
      for (let i = 0; i < list.length; i++) {
        const { file } = list[i];
        const key = `${seqUrl}/${i + 1}.${seqSuffix}`;
        await uploadToQiniu({
          tokenType: QiniuTokenType.media,
          file,
          key,
        });
        setItemMap((draft) => {
          const item = draft.get(id);
          if (item) item.progress = toPercent((i + 1) / list.length);
        });
      }
      await addToLib({
        url: seqUrl,
        type: LibApiType.seqFrame,
        frame: list.length,
        suffix: seqSuffix,
        size: totalSize,
        name: seqName,
        albumId,
      });
      setItemMap((draft) => {
        draft.delete(id);
      });
    } catch (e) {
      setItemMap((draft) => {
        draft.delete(id);
      });
      if (e.name === 'Warning') {
        sendWarning(e.message);
      } else {
        sendError(e.message);
      }
    }
    queryClient.refetchQueries([queryKeys.libListSeqFrame, 'items']);
    refetchLibSpace();
  };

  return (
    <UploadButton
      className="ml-auto"
      itemMap={itemMap}
      onClick={handleUplpad}
    />
  );
}

export default UploadButtonSeqFrame;
